import isEqual from "lodash/isEqual";
import { Vue, Options } from "vue-class-component";
import { AxiosResponse } from "axios";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
@Options({})
abstract class BaseForm<TModel, TCreateModel, TUpdateModel> extends Vue {
  route = useRoute();
  router = useRouter();
  toast = useToast();
  protected savedEntity: TModel = {} as TModel;
  protected entity: TModel = {} as TModel;
  protected defaultEntity: TModel = {} as TModel;
  protected id!: string;
  protected formReady = false;
  protected formLoading = false;
  protected abstract meta: {
    gridRoute: string;
    formEditRoute: string;
    entityTypeName: string;
  };

  get isDirty() {
    return !isEqual(this.entity, this.savedEntity);
  }

  get isCreateMode(): boolean {
    return this.route.params.id === undefined;
  }

  abstract getRequest(id: string): Promise<AxiosResponse<TModel>>;
  abstract createRequest(entity: TModel): Promise<AxiosResponse<TModel>>;
  abstract updateRequest(id: string, entity: TModel): Promise<AxiosResponse<TModel>>;

  abstract mapToCreateDto(entity: TModel): TCreateModel;
  abstract mapToUpdateDto(entity: TModel): TUpdateModel;

  protected async onEntityLoaded() {
    //
    this.formReady = true;
  }

  beforeMount() {
    this.id = this.route.params.id as string;
  }

  mounted() {
    this.loadData().catch(e => {
      console.error("Failed to load form data", e);
      this.toast.error("Could not load the data. Please try again.");
    });
  }
  async loadData() {
    if (this.id) {
      this.formLoading = true;
      await this.getRequest(this.id)
        .then(response => {
          this.entity = response.data;
          this.savedEntity = structuredClone(response.data);
        })
        .finally(() => {
          this.formLoading = false;
        });
    } else {
      this.entity = structuredClone(this.defaultEntity);
      this.savedEntity = structuredClone(this.defaultEntity);
    }

    await this.onEntityLoaded();
  }
  async onSave() {
    try {
      this.formLoading = true;
      const form = this.$refs.form as HTMLFormElement;
      if (form.value) {
        if (!form.value.checkValidity()) {
          this.toast.warning("Invalid Form Value", {
            toastClassName: "custom-warning-toast",
            timeout: 5000
          });
          return;
        }
      }

      if (this.id) {
        await this.executeApi(this.updateRequest(this.id, this.entity)).finally(() => {
          this.formLoading = false;
        });
      } else {
        await this.executeApi(this.createRequest(this.entity), "Created").finally(() => {
          this.formLoading = false;
        });
      }
    } catch (ex) {
      console.log(ex);
    }
  }

  protected async executeApi(apiFunc: Promise<AxiosResponse<TModel | void>>, operation?: string) {
    await apiFunc
      .then(async (response: AxiosResponse<TModel | void> | AxiosResponse) => {
        const msg = `${this.meta.entityTypeName} Successfully ${operation ?? "Updated"}`;
        this.toast.success(msg, {
          toastClassName: "custom-success-toast",
          timeout: 5000
        });

        // If created, change route
        if (!this.id && response.data.id) {
          await this.router.push({
            name: this.meta.formEditRoute,
            params: {
              id: response.data.id
            }
          });
          this.id = response.data.id;
        }

        this.savedEntity = structuredClone(response.data);
        this.entity = structuredClone(response.data);
      })
      .catch(e => {
        if (e.errorMsg == null) {
          const msg = `Cannot Update ${this.meta.entityTypeName}`;
          this.toast.error(msg, {
            toastClassName: "custom-error-toast",
            timeout: 5000
          });
        } else {
          this.toast.error(e.errorMsg, {
            toastClassName: "custom-error-toast",
            timeout: 5000
          });
        }
      });
  }

  async onClose() {
    await this.router.push({
      name: this.meta.gridRoute
    });
  }
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default class BaseFormMixin<TModel, TCreateModel, TUpdateModel> extends BaseForm<
  TModel,
  TCreateModel,
  TUpdateModel
> {}
